import { createAsyncThunk } from '@reduxjs/toolkit';
import { getPersonalWorkCalendarData } from 'scenes/WorkDemo/services/getPersonalWorkCalendarData.service';
import * as reports from 'scenes/WorkDemo/utils/report-generation/reports';
import { selectProjects } from 'store/projects/selectors/projectsSelectors';
import { postReportDistanceMatrix } from '../services/postReportDistanceMatrix.service';
import {
  applyDistances,
  buildEmployeeYearTripData,
  findResourceWithAddress,
} from '../utils/buildEmployeeYearTripData.util';
import { loadResourcePictureDataUrl } from '../utils/loadResourcePictureDataUrl.util';

/**
 * Vuosiraportti verottajalle — the employee's annual commute report.
 *
 * This ran as the reportGen Lambda until U-07. The pipeline is the same one it
 * used (records -> commutes per worksite -> road distances -> PDF); what moved
 * is where each step happens. Only the distance lookup still needs a server,
 * because Google refuses that endpoint to browsers.
 */
export const generateEmployeeYearReport = createAsyncThunk(
  'reports/generateEmployeeYearReport',
  async (
    { employerId, employer, fromDate, toDate, settings, requestFile },
    { getState, rejectWithValue }
  ) => {
    const state = getState();

    const { records = [], resources = [] } = await getPersonalWorkCalendarData(
      fromDate,
      toDate
    );

    const resourceWithAddress = findResourceWithAddress(resources);

    if (!resourceWithAddress) {
      // The report measures every commute from the employee's home, so without
      // an address there is nothing to compute - the Lambda bailed here too.
      return rejectWithValue('report.employee_year_no_address');
    }

    // requestFile comes from the LixaniFiles React context, so the dialog
    // hands it in rather than the thunk reaching for it.
    const profileImage = await loadResourcePictureDataUrl(
      resources,
      requestFile
    );

    const projectsById = selectProjects(state);

    const { projects, reportDaysSum, employerRecordCount } =
      buildEmployeeYearTripData({
        records,
        resources,
        projectsById,
        employerId,
      });

    if (employerRecordCount === 0) {
      return rejectWithValue('report.employee_year_no_records');
    }

    if (projects.length === 0) {
      return rejectWithValue('report.employee_year_no_projects_with_address');
    }

    const distances = await postReportDistanceMatrix(
      resourceWithAddress,
      projects
    );

    const { recordDistanceMap, reportTripSum, reportDistanceSum } =
      applyDistances(projects, distances);

    const reportData = {
      recordDistanceMap,
      // user/get-user returns a projection the GraphQL schema shaped - it
      // carries taxNumber but not the name or date of birth the report prints.
      // Those live on the resource, which is also where the home address
      // comes from.
      user: {
        ...state.root.user,
        firstName: resourceWithAddress.firstName,
        lastName: resourceWithAddress.lastName,
        dateOfBirth: resourceWithAddress.dateOfBirth,
      },
      resourceWithAddress,
      employerObject: projectsById[employerId] || employer,
      reportDaysSum,
      reportTripSum,
      reportDistanceSum,
      fromDate,
      toDate,
      profileImage,
    };

    const document = await reports.employee_year_report.createReport(
      reportData,
      settings
    );

    const { documentName, documentFilename } =
      reports.employee_year_report.docName(settings);

    return { document, documentName, documentFilename };
  }
);
